import { useCallback } from 'react';
import { Actor } from '../components/ActorsList/ActorsListItem';
import { ActorVariant } from '../../../../server/graphql/__generated.types';
import { usePromptsContext } from '../../../context/prompts/PromptsContext';
import {
  useIsManagedUser,
  useIsTrialUser,
} from '../../../context/user/UserContext';
import usePromptForSubscriptionUpgrade from '../../Account/usePromptForSubscriptionUpgrade';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';
import useActiveActor from './useActiveActor';

export interface useAvatarProps {
  setActiveAvatar: ReturnType<typeof useActiveActor>['setActiveActor'];
}

const useAvatarClickHandler = (props: useAvatarProps) => {
  const { setActiveAvatar } = props;
  const [eventTracking] = useEventsContext();
  const [, dispatch] = usePromptsContext();
  const isManagedUser = useIsManagedUser();
  const isTrialUser = useIsTrialUser();
  const upgradePath = 'Dashboard > Actors';
  const [openUpgradePrompt] = usePromptForSubscriptionUpgrade({
    upgradeContext: 'default',
    upgradePath,
  });
  const onClickLockedAvatar = useCallback(() => {
    if (isManagedUser) {
      return;
    }
    if (isTrialUser) {
      dispatch({
        type: 'OpenSubscriptionTiersPrompt',
        payload: {
          upgradeContext: 'increase_access',
          upgradePath,
        },
      });
    } else {
      openUpgradePrompt();
    }
  }, [dispatch, isManagedUser, isTrialUser, openUpgradePrompt]);

  const handleAvatarClick = useCallback(
    (
      actor: Actor,
      location: 'list' | 'modal',
      variant?: ActorVariant,
      variantVersion?: string,
      onClose?: () => void
    ) => {
      if (actor.accessLevel === 'LOCKED') {
        onClickLockedAvatar();
      } else {
        eventTracking.track({
          type: EventName.ActorSelected,
          data: {
            actorName: actor.name,
            actorId: `${actor.id}`,
            location,
          },
        });
        setActiveAvatar(actor, variant);
        if (onClose) onClose();
      }
    },
    [onClickLockedAvatar, setActiveAvatar, eventTracking]
  );

  return {
    handleAvatarClick,
  };
};

export default useAvatarClickHandler;
